import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useLenis } from "../hooks/useLenis";
import { NAV, STUDIO } from "../data/canarts";

const easeOut = [0.22, 1, 0.36, 1];

// Replace these two with the final Project Overview images when ready.
const OVERVIEW_IMAGES = {
  studio: STUDIO.hero,
  live: "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/nstw0ixg_image.png",
};

const AUDIENCES = ["Artists", "Singers", "Producers", "DJs", "Content Creators"];

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
      <Link
        to="/"
        data-testid="logo-link"
        className="font-display text-2xl md:text-3xl font-bold tracking-tight"
      >
        CAN<span className="text-primary">ARTS</span>
      </Link>
      <nav className="hidden md:flex items-center gap-10">
        {NAV.map((n) =>
          n.route ? (
            <Link
              key={n.href}
              to={n.href}
              className="text-sm uppercase tracking-[0.2em] text-foreground transition-colors duration-300"
            >
              {n.label}
            </Link>
          ) : (
            <a
              key={n.href}
              href={`/${n.href}`}
              className="text-sm uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              {n.label}
            </a>
          )
        )}
      </nav>
      <Link
        to="/"
        data-testid="back-home-btn"
        className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white hover:text-black transition-colors duration-300"
      >
        <ArrowLeft size={15} /> Home
      </Link>
    </div>
  </header>
);

export default function WhoWeAre() {
  useLenis();
  useEffect(() => {
    document.documentElement.classList.add("dark");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App relative" data-testid="who-we-are-page">
      <div className="grain-overlay" />
      <Header />

      <main className="pt-16 md:pt-20">
        {/* Hero / intro */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: easeOut }}
                className="text-sm uppercase tracking-[0.35em] text-secondary mb-6"
              >
                / Who We Are
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: easeOut, delay: 0.1 }}
                className="font-display font-bold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mb-8"
              >
                The <span className="text-primary">Vision</span>
              </motion.h1>
              <div className="space-y-5 max-w-xl text-base md:text-lg font-light text-muted-foreground">
                <Reveal delay={0.15}>
                  <p>
                    CANARTS is a creative music company focused on music, branding
                    and artist development. We help artists and music projects
                    build strong identities through music production, creative
                    direction, digital strategy and audiovisual content.
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p>
                    We believe an artist's growth comes not only from great music,
                    but from building an authentic vision and creating a real
                    connection with their audience.
                  </p>
                </Reveal>
                <Reveal delay={0.25}>
                  <p>
                    From developing the sound and artistic identity to branding,
                    digital growth and live experiences, CANARTS supports artists
                    through different stages of their journey.
                  </p>
                </Reveal>
              </div>
              <Reveal delay={0.3}>
                <a
                  href="/#contact"
                  data-testid="whoweare-contact-btn"
                  className="inline-flex items-center gap-2 mt-10 bg-primary text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white hover:text-black transition-colors duration-300"
                >
                  Work with CANARTS <ArrowUpRight size={16} />
                </a>
              </Reveal>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: easeOut, delay: 0.15 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-secondary/10 blur-3xl rounded-full" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5] lg:aspect-[3/4]">
                <img
                  src={STUDIO.hero}
                  alt="CANARTS Studios"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Project Overview */}
        <section
          id="project-overview"
          data-testid="project-overview"
          className="border-t border-white/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-28">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-6">
                / Project Overview
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] max-w-4xl mb-6">
                Where Creativity Meets{" "}
                <span className="text-primary">Opportunity.</span>
              </h2>
              <p className="text-lg md:text-xl font-light text-muted-foreground max-w-2xl mb-8">
                CANARTS is built for artists and creatives who want to turn their
                ideas into something bigger.
              </p>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="flex flex-wrap gap-2.5 mb-14 md:mb-16">
                {AUDIENCES.map((t) => (
                  <span
                    key={t}
                    className="text-xs uppercase tracking-[0.2em] border border-white/15 text-muted-foreground px-4 py-2 rounded-full hover:border-secondary/50 hover:text-secondary transition-colors duration-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>

            <div className="grid lg:grid-cols-12 gap-5 md:gap-6">
              <Reveal className="lg:col-span-7">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 h-[320px] md:h-[520px] group">
                  <img
                    src={OVERVIEW_IMAGES.studio}
                    alt="Music creation and production"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute bottom-5 left-5 text-xs uppercase tracking-[0.25em] text-white/90">
                    Studio &amp; Production
                  </span>
                </div>
              </Reveal>
              <Reveal className="lg:col-span-5" delay={0.1}>
                <div className="relative overflow-hidden rounded-2xl border border-white/10 h-[320px] md:h-[520px] group">
                  <img
                    src={OVERVIEW_IMAGES.live}
                    alt="Live performance and stage experience"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <span className="absolute bottom-5 left-5 text-xs uppercase tracking-[0.25em] text-white/90">
                    Live &amp; Stage
                  </span>
                </div>
              </Reveal>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-16 mt-14 md:mt-20 items-center">
              <Reveal>
                <p className="text-base md:text-lg font-light text-muted-foreground">
                  We connect creativity with a business-driven vision, bringing
                  together the tools, strategy and creative support artists need to
                  stand out in an increasingly competitive music industry.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="font-display text-2xl md:text-3xl font-semibold leading-snug border-l-2 border-primary pl-6">
                  From creating the sound to building the brand —{" "}
                  <span className="text-secondary">
                    we help turn ideas into projects built to grow.
                  </span>
                </p>
              </Reveal>
            </div>
          </div>
        </section>
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-white/10 aspect-[21/9]">
              <img
                src={STUDIO.banner}
                alt="CANARTS Studios space"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </section>

        {/* Why CANARTS */}
        <section
          id="why-canarts"
          data-testid="why-canarts"
          className="border-t border-white/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-28">
            <Reveal>
              <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-6">
                / What Makes Us Different
              </p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] max-w-3xl mb-8">
                You Create. <span className="text-primary">We Build With You.</span>
              </h2>
              <div className="max-w-2xl space-y-4 text-base md:text-lg font-light text-muted-foreground mb-16">
                <p>
                  At CANARTS, artists don't have to figure everything out alone.
                </p>
                <p>
                  We believe creative talent needs more than a service — it needs
                  the right team around it. From the first idea to the finished
                  project, we work alongside artists through the creative,
                  production and development process.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {[
                {
                  n: "01",
                  title: "More Than a Studio",
                  body: "We don't just record the project. We help develop the sound, concept and creative direction behind it.",
                },
                {
                  n: "02",
                  title: "One Creative Ecosystem",
                  body: "Music production, artist development, branding, content and strategy can all work together under one creative vision.",
                },
                {
                  n: "03",
                  title: "Built Together",
                  body: "We work alongside our artists instead of simply handing them a finished product and sending them on their way.",
                },
              ].map((p, i) => (
                <Reveal key={p.n} delay={i * 0.08}>
                  <div className="h-full rounded-2xl border border-white/10 p-7 md:p-8 hover:border-secondary/50 transition-colors duration-300">
                    <p className="font-display text-3xl font-light text-primary mb-5">
                      {p.n}
                    </p>
                    <h3 className="font-display text-xl md:text-2xl font-semibold mb-3">
                      {p.title}
                    </h3>
                    <p className="text-sm md:text-base font-light text-muted-foreground leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <p className="font-display text-2xl md:text-4xl font-semibold leading-snug border-l-2 border-primary pl-6 mt-16 max-w-3xl">
                Your vision. Your identity. Your career.{" "}
                <span className="text-secondary">
                  You don't have to build it alone.
                </span>
              </p>
            </Reveal>
          </div>
        </section>

      </main>

      <footer className="border-t border-white/10 py-10 mt-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <Link to="/" className="font-display text-lg text-foreground">
            CAN<span className="text-primary">ARTS</span>
          </Link>
          <p>© {new Date().getFullYear()} Canarts · Toronto. All rights reserved.</p>
          <Link
            to="/admin"
            data-testid="footer-admin-login"
            className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground/80 hover:text-primary transition-colors duration-300"
          >
            Admin Login
          </Link>
        </div>
      </footer>
    </div>
  );
}
